import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges ,ViewChild} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { Observable, Subscription } from 'rxjs';
import { APP_ICONS } from 'src/app/icons/icon-provider';
import { ApiService } from 'src/app/services/api.service';
import { AppSettings } from 'src/app/services/AppSettings';
import { CandidateService } from 'src/app/services/CandidateService';
import { SlidePanelTab } from 'src/app/services/slide-panel.service';
import { UtilService } from 'src/app/services/util.service';
import { PricePlanService } from 'src/app/components/Price-subscritions/priceplanService';
import { MessageWidgetComponent } from 'src/app/widget/message-widget/message-widget.component';
import { CpAiInsightsComponent } from '../shared/cp-ai-insights/cp-ai-insights.component';
import { CpHeaderComponent } from '../shared/cp-header/cp-header.component';
import { CpRecStripComponent } from '../shared/cp-rec-strip/cp-rec-strip.component';
import { CpResumeCardComponent } from '../shared/cp-resume-card/cp-resume-card.component';
import { ConnectionTemplateComponent } from 'src/app/templates/connection-template/connection-template.component';
import { CommonSkeletonComponent } from 'src/app/skeleton/common-skeleton/common-skeleton.component';
import { NgxSummernoteModule } from 'ngx-summernote';
import Swal from 'sweetalert2';

/**
 * V3 candidate profile slider — 580px right-edge drawer, 4 tabs.
 * Matches `gigsumo_job_center_v2(16).html` `.cp-slider` (lines 1678-1959).
 *
 * Data flow:
 *   1. Parent hands in `candidateSummary` (row-level data) — we paint
 *      immediately so the drawer feels instant.
 *   2. ngOnInit fires `CandidateService.findByCandidateId(id)` to hydrate
 *      the full candidate model; Tab 1 upgrades to richer content once
 *      the response resolves.
 *
 * Tab lazy-rendering: `*ngIf="activeTab === 'X'"` on every tab body so
 * AI fetch + Chat WebSocket only fire when the user actually lands on the
 * tab. When `initialTab === 'ai'` (match-badge click), the AI tab mounts
 * on first render and the fetch fires immediately.
 */
@Component({
  selector: 'app-candidate-profile-slider',
  standalone: true,
  imports: [
    CommonModule,
    NgIconComponent,
    CpHeaderComponent,
    CpRecStripComponent,
    CpAiInsightsComponent,
    CpResumeCardComponent,
    MessageWidgetComponent,
    ConnectionTemplateComponent,
    CommonSkeletonComponent,
    NgxSummernoteModule,
  ],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './candidate-profile-slider.component.html'
})
export class CandidateProfileSliderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() candidateSummary: any = null;
  @Input() jobId: string | undefined;
  /** The job whose applicants/invites this slider was opened from. Drives
   *  the Pay Rate range (job's posted range). Null when opened without a job
   *  context (e.g. candidate listing) → Pay Rate falls back to the
   *  candidate's own target range. */
  @Input() jobData: any = null;
  @Input() initialTab: SlidePanelTab | undefined;
  @Input() pageFrom: string | undefined;
  @Input() hideAiInsights = false;
  @Input() hideChat = false;

  @Output() closeRequested = new EventEmitter<void>();

  /** Hydrated from `findByCandidateId`; Tab 1 reads `candidate` which falls
   *  back to `candidateSummary` while the fetch is in-flight. */
  fullProfile: any = null;
  loadingProfile = false;
chatLoadingIsOwner = false;
 
  activeTab: SlidePanelTab = 'profile';

  // Chat state — mirrors candidate-card-table-view. `messagemodelflag`
  // drives the `<app-message-widget *ngIf>` and `messageData` is the
  // mutated item passed as `messageDataPass`. Keeping the same property
  // names makes drift easier to catch if either side is updated later.
  messagemodelflag = false;
  messageData: any = null;
  seletedChatItem: any = null;

  /** Shared download-in-flight flag. Owned here (not in cp-resume-card)
   *  because BOTH the header "Download Resume" action and the Resume tab's
   *  card button trigger the same download and must disable/spin together —
   *  and because the tab pane is `*ngIf`-lazy, so card-local state would be
   *  lost (or unreachable) when the tab isn't mounted. */
  resumeDownloading = false;

  readonly url = AppSettings.ServerUrl;
  private readonly viewerId = localStorage.getItem('userId');
  @ViewChild(MessageWidgetComponent) messageWidget?: MessageWidgetComponent;  

  constructor(
    private candidateService: CandidateService,
    private apiService: ApiService,
    private util: UtilService,
    private planService: PricePlanService,
  ) { }

  ngOnInit(): void {
    this.activeTab = this.initialTab || 'profile';
    this.loadFullProfile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When the service state changes (user opens slider again on a
    // different candidate), ngOnChanges fires — rehydrate.
    if (changes['candidateSummary'] && !changes['candidateSummary'].firstChange) {
      this.fullProfile = null;
      this.activeTab = this.initialTab || 'profile';
      this.messagemodelflag = false;
      this.loadFullProfile();
    }
  }

  ngOnDestroy(): void {
    this.profileSub?.unsubscribe();
  }

  /** In-flight findByCandidateId. The drawer is reused (not re-created)
   *  across openings, so clicking row after row in quick succession used to
   *  leave several fetches racing and whichever settled last won — the
   *  header could end up hydrated with a candidate the user had already
   *  clicked past. Cancel the previous one before issuing the next. */
  private profileSub?: Subscription;

  private loadFullProfile(): void {
    const id = this.candidateSummary?.candidateId;
    if (!id) return;
    this.profileSub?.unsubscribe();
    this.loadingProfile = true;
    this.profileSub = this.candidateService.findByCandidateId(id).subscribe({
      next: (data: any) => {
        // The findByCandidateId service returns the API's `response.data`
        // payload, which wraps the candidate fields under `.candidateData`
        // (parity with candidate-profile-summary.component.ts:519). Without
        // unwrapping, `fullProfile.candidateEmailId` was undefined and the
        // header email row (and other deep fields) silently dropped —
        // most visible when the slider is opened from job-card with only
        // a candidateId in the summary.
        this.fullProfile = data?.candidateData ?? data ?? null;
        this.loadingProfile = false;
      },
      error: () => { this.loadingProfile = false; }
    });
  }

  /** Merged candidate — summary fields persist if the detailed fetch is
   *  missing them (common when the backend response shape differs from
   *  the row shape). fullProfile fields override summary on overlap.
   *
   *  Memoised: returning a fresh spread on every change-detection tick
   *  would discard local mutations made by child components — e.g.
   *  `<app-connection-template>` flips `values.connectionStatus` to
   *  'CONNECTED' on accept and relies on that mutation surviving until
   *  the API refresh. Without memoisation the button reverts to the
   *  pre-accept state on the next CD pass. */
  private _mergedCandidate: any = null;
  private _mergedSummaryRef: any = null;
  private _mergedProfileRef: any = null;
  get candidate(): any {
    if (this._mergedSummaryRef !== this.candidateSummary
        || this._mergedProfileRef !== this.fullProfile) {
      this._mergedCandidate = { ...(this.candidateSummary ?? {}), ...(this.fullProfile ?? {}) };
      this._mergedSummaryRef = this.candidateSummary;
      this._mergedProfileRef = this.fullProfile;
    }
    return this._mergedCandidate;
  }

  private readonly monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  /** Education ordered MOST-RECENT-FIRST by the "To" (end) date — the LinkedIn /
   *  Naukri convention. e.g. MCS (…2026) → BSC (…2018) → 12th (…2017).
   *
   *  Sorted on dates, NOT the degree text: `degree` is a free-text input (no
   *  dropdown / list value), so it can't be ranked reliably across locales —
   *  "B.Tech", "Bachelor of Science", "BSc (Hons)" and "Licenciatura" are all the
   *  same tier, and keyword matching misfires (the resume-mapper's
   *  `degree.includes('ba')` also matches "MBA"). Education is sequential, so the
   *  end date reproduces the degree hierarchy for free, in any country.
   *
   *  The END date is the key, not the start: with real data the start date can
   *  mislead (a 12th row starting 2016 would jump above a BSC that started 2014
   *  but finished 2018). Completion order is what these sites actually show.
   *
   *  "Currently Pursuing" (the row rendered as "Present") is pinned to the top,
   *  mirroring currentOrganization in sortedWorkExperiences. This must be an
   *  EXPLICIT check, not "it has no end date": unlike work experience, the
   *  education save does NOT null endMonth/endYear when currentlyPursued is
   *  ticked, so a "Present" row can still carry a stale end date and would
   *  otherwise be ranked by it (a Present row ending 2015 would sink to the
   *  bottom while displaying "Present").
   *
   *  Memoised like `candidate` above — a getter returning a fresh array on every
   *  change-detection tick would churn the *ngFor. Sorts a COPY, since sort()
   *  mutates in place and the underlying list must keep its own order. */
  private _sortedEducation: any[] | null = null;
  private _sortedEducationRef: any = null;
  get sortedEducation(): any[] {
    const list = this.candidate?.educationDetails;
    if (!Array.isArray(list)) { return []; }
    if (this._sortedEducationRef !== list) {
      this._sortedEducation = [...list].sort((a, b) => {
        const aCurrent = a?.currentlyPursued === true || a?.currentlyPursued === 'true';
        const bCurrent = b?.currentlyPursued === true || b?.currentlyPursued === 'true';
        if (aCurrent !== bCurrent) { return aCurrent ? -1 : 1; }
        // Descending (b - a) → latest completion first.
        const endDiff = this.toSortableDate(b?.endYear, b?.endMonth)
          - this.toSortableDate(a?.endYear, a?.endMonth);
        if (endDiff !== 0) { return endDiff; }
        // Same end → whichever started later comes first.
        return this.toSortableDate(b?.startYear, b?.startMonth)
          - this.toSortableDate(a?.startYear, a?.startMonth);
      });
      this._sortedEducationRef = list;
    }
    return this._sortedEducation ?? [];
  }

  /** Work experience ordered the same way as `sortedEducation` — the LinkedIn /
   *  Naukri convention — with the CURRENT organization pinned to the top:
   *    1. currentOrganization first
   *    2. then latest "To" (end) date first
   *    3. then latest start date first
   *
   *  The explicit currentOrganization check is belt-and-braces: the save nulls
   *  endMonth/endYear for a current org, so it would already sort first via the
   *  missing-date sentinel — but this stays correct even for legacy rows that
   *  kept an end date while flagged current.
   *
   *  Memoised + sorts a COPY, for the same reasons as sortedEducation. */
  private _sortedWorkExperiences: any[] | null = null;
  private _sortedWorkExperiencesRef: any = null;
  get sortedWorkExperiences(): any[] {
    const list = this.candidate?.workExperiences;
    if (!Array.isArray(list)) { return []; }
    if (this._sortedWorkExperiencesRef !== list) {
      this._sortedWorkExperiences = [...list].sort((a, b) => {
        const aCurrent = a?.currentOrganization === true || a?.currentOrganization === 'true';
        const bCurrent = b?.currentOrganization === true || b?.currentOrganization === 'true';
        if (aCurrent !== bCurrent) { return aCurrent ? -1 : 1; }
        // Descending (b - a) → most recent first.
        const endDiff = this.toSortableDate(b?.endYear, b?.endMonth)
          - this.toSortableDate(a?.endYear, a?.endMonth);
        if (endDiff !== 0) { return endDiff; }
        return this.toSortableDate(b?.startYear, b?.startMonth)
          - this.toSortableDate(a?.startYear, a?.startMonth);
      });
      this._sortedWorkExperiencesRef = list;
    }
    return this._sortedWorkExperiences ?? [];
  }

  /** year + month → one sortable number (`year * 100 + monthIndex`), so the year
   *  always dominates the month.
   *
   *  Missing / unparsable dates return a sentinel that sorts FIRST in this
   *  descending order — deliberate: a "Currently Pursuing" / current-organization
   *  row has no end date and is the most recent, so it belongs at the top.
   *  Returning NaN here would poison the comparator and corrupt the ordering. */
  private toSortableDate(year: any, month: any): number {
    const y = parseInt(year, 10);
    if (isNaN(y)) { return Number.MAX_SAFE_INTEGER; }
    const m = this.monthOrder.indexOf(month);
    return y * 100 + (m >= 0 ? m : 0);
  }

  /** Tenure of a work experience, from its start date to its end date (or to the
   *  CURRENT month, for a current organization), formatted as "2 years 2 months".
   *
   *  The span is INCLUSIVE of both the start and the end month — a month worked in
   *  any part counts as a month worked, which is the convention every job board
   *  uses. So Aug 2023 – Dec 2023 is 5 months (Aug, Sep, Oct, Nov, Dec), not 4, and
   *  a Jun 2024 – Present read in Jul 2026 is 26 months = "2 years 2 months". The
   *  earlier non-inclusive count was short by exactly one month on every entry.
   *
   *  Whole years print alone ("1 year", not "1 year 0 months"); a sub-year span
   *  prints months alone ("5 months").
   *
   *  Returns '' when a needed date is missing/unparsable, or when the end precedes
   *  the start, so the template hides the segment rather than showing nonsense. */
  getExperienceDuration(exp: any): string {
    if (!exp?.startMonth || !exp?.startYear) { return ''; }
    const startY = parseInt(exp.startYear, 10);
    const startM = this.monthOrder.indexOf(exp.startMonth);
    if (isNaN(startY) || startM < 0) { return ''; }

    let endY: number;
    let endM: number;
    if (exp.currentOrganization) {
      const now = new Date();
      endY = now.getFullYear();
      endM = now.getMonth();
    } else {
      endY = parseInt(exp.endYear, 10);
      endM = this.monthOrder.indexOf(exp.endMonth);
      if (isNaN(endY) || endM < 0) { return ''; }
    }

    // +1 counts BOTH endpoint months (start in and out in the same month = 1 month).
    const months = (endY - startY) * 12 + (endM - startM) + 1;
    if (months <= 0) { return ''; }

    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    const parts: string[] = [];
    if (years > 0) { parts.push(`${years} ${years === 1 ? 'year' : 'years'}`); }
    if (remMonths > 0) { parts.push(`${remMonths} ${remMonths === 1 ? 'month' : 'months'}`); }
    return parts.join(' ');
  }

  selectTab(tab: SlidePanelTab): void { this.activeTab = tab; }
  // selectTab(tab: SlidePanelTab): void {
  // if (this.activeTab === 'chat' && tab !== 'chat') {
  //   this.messageWidget?.resetToContactList();
  // }
  // this.activeTab = tab;
// }

  /** Chat tab (wireframe TAB 4). Swaps the pane immediately (a "Opening
   *  chat…" placeholder shows) and runs the exact same gated flow as the
   *  old header-button chat — plan check → connection check → openMessage()
   *  flips `messagemodelflag`, which now renders the widget INSIDE the pane
   *  instead of as a floating bubble. Re-clicking the tab while the widget
   *  is already open must not re-run the flow (openMessage resets the
   *  widget with a 1s delay, which would blank an active conversation). */
  openChatTab(): void {
    // Own candidate with no thread → chat is a disabled placeholder; nothing
    // to open (defense-in-depth; the tab/button are also [disabled]).
    if (this.chatDisabled) { return; }
    // Widget already loaded → just reveal the Chat tab, no re-check.
    if (this.messagemodelflag) {
      this.activeTab = 'chat';
      return;
    }
    // Fresh open — do NOT switch to the Chat tab yet. The gated flow only
    // activates the tab once chat actually opens (see openMessage). If the
    // interaction/plan is expired, openMessagecheckInteraction shows the
    // limit popup and never calls openMessage, so we stay on the current tab
    // instead of stranding the user on an empty skeleton pane.
    this.sendMessage(this.candidate);
  }

  /** Own candidate with no existing chat thread → chat is a disabled
   *  placeholder. Owners never initiate a chat (they only respond to threads
   *  others started), so with no history there is nothing to open. Mirrors the
   *  card's owner + no-history state; gates the header Chat action + Chat tab. */
  get chatDisabled(): boolean {
    return this.isContractOrBillingView
      || (this.isOwnCandidate && (this.candidate?.chatHistoryCount || 0) === 0);
  }

  /** Chat (header action + Chat tab) is always off inside Contract Center and
   *  Billing Center; those pages tag the slider with these pageFrom values. */
  get isContractOrBillingView(): boolean {
    return this.pageFrom === 'contract-candidates'
      || this.pageFrom === 'contract-work-orders'
      || this.pageFrom === 'billing-center';
  }

  /** AI match ring (header %) and the AI Insights tab are hidden when the
   *  opener asks for it (hideAiInsights) and always inside Contract Center /
   *  Billing Center (2026-09-08) — those views never show AI scoring. */
  get aiHidden(): boolean {
    return this.hideAiInsights || this.isContractOrBillingView;
  }

  /** Chat (header action + Chat tab) is removed entirely — not just
   *  disabled — when the opener asks for it (hideChat) and always inside
   *  Contract Center / Billing Center (2026-09-08). */
  get chatHidden(): boolean {
    return this.hideChat || this.isContractOrBillingView;
  }

  /** CEIPAL Free plan detection — hide match scores and show popup for AI features. */
  get isCeipalFreePlan(): boolean {
    const membershipType = localStorage.getItem('MemberShipType');
    return this.jobData?.source === 'CEIPAL' && membershipType === 'Free';
  }

  showCeipalFreePopup(): void {
    Swal.fire({
      icon: 'info',
      title: '',
      text: 'AI features are not available for CEIPAL Free plan. Please upgrade your plan to access AI-powered recommendations.',
      showConfirmButton: true,
      confirmButtonText: 'OK'
    });
  }

  onMatchRingClick(): void {
    if (this.isCeipalFreePlan) {
      this.showCeipalFreePopup();
      return;
    }
    this.selectTab('ai');
  }
  onHeaderAction(key: string): void {
    if (key === 'download') {
      // Header shortcut — swaps to Tab 2 where cp-resume-card's own
      // Download button owns the actual download flow.
      this.triggerResumeDownload();
    } else if (key === 'chat') {
      // Wireframe parity: the header Chat button lands on the Chat TAB
      // (mock line 1703 does switchCpTab('chat')). Same gated flow.
      this.openChatTab();
    } else if (key === 'share') {
      this.onShare();
    }
  }

  /** Share the candidate via a login-bridge URL — mirrors the SSR pattern
   *  (job-listing.component.ts:35-43 `getCandidateParams`). The recipient
   *  hits /auth/login; login.component's `absolutePath=true&data=…` branch
   *  (login.component.ts:153) rebuilds the destination URL after login.
   *  Logged-in recipients are auto-bounced past login by AfterLoginGuard
   *  straight to /newcandidates?candidateId=…&tabName=…, so the same URL
   *  works for both authed and unauthed viewers.
   *  Web Share API → falls back to clipboard. */
  onShare(): void {
    const candidateId = this.candidate?.candidateId;
    if (!candidateId) return;
    const data = {
      candidateId,
      tabName: 'profileSummary',
      From: 'CANDIDATES_FROM_CONNECTION'
    };
    // Hash routing (environment.useHash = true) needs `/#/` before /auth/login.
    const url = `${window.location.origin}/#/auth/login`
              + `?redirectTo=/newcandidates`
              + `&absolutePath=true`
              + `&data=${encodeURIComponent(JSON.stringify(data))}`;
    const title = this.candidateName || 'Candidate';
    const nav: any = window.navigator;
    if (nav?.share) {
      nav.share({ title, url }).catch(() => { /* user cancelled */ });
    } else if (nav?.clipboard) {
      nav.clipboard.writeText(url);
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Chat flow — ported verbatim from
  // `candidate-card-table-view.component.ts:452-565`. Kept 1:1 so the
  // slider's chat behaviour is identical to clicking the listing's chat
  // button: same plan/interaction gate, same connection check, same
  // payload shape, same close semantics. Any change the listing gets
  // should be mirrored here.
  // ──────────────────────────────────────────────────────────────────────

  async sendMessage(item: any): Promise<void> {
    await this.openMessagecheckInteraction(item);
  }

  checkConnectionStatus(values: any): Observable<any> {
    const apiURL = 'user/checkConnectionStatus';
    const params = new HttpParams()
      .set('userId', this.viewerId || '')
      .set('connnectorId', values?.createdBy || '');
    return this.apiService.query(`${apiURL}?${params.toString()}`);
  }

  async openMessagecheckInteraction(seletedItem: any): Promise<void> {
    // Universal first-interaction: skip the ACTIVE_INTERACTIONS gate when this
    // pair's interaction was already consumed (existing chat / prior invite /
    // resume-request — backend `alreadyConsumed`). Fails safe to gating.
    if (await this.planService.isInteractionAlreadyConsumed(seletedItem?.candidateId, 'CANDIDATE')) {
      this.chatuser(seletedItem);
      return;
    }
    try {
      this.util.startLoader();
      const userData: any = await this.planService.UPDATE_USERDETAILS('ACTIVE_INTERACTIONS');
      const { userDetail, isExpired, membershipType } = userData || {};
      let availablePoints = null;
      let actualPoints = null;
      let utilizedPoints = null;
      let promotional = null;

      if (userDetail?.userPlanAndBenefits?.benefitsUsages) {
        userDetail.userPlanAndBenefits.benefitsUsages.forEach((ele: any) => {
          if (ele.benefitKey === 'ACTIVE_INTERACTIONS') {
            availablePoints = ele.available;
            actualPoints = ele.actual;
            utilizedPoints = ele.utilized;
            promotional = ele.promotional;
          }
        });
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
        return;
      }

      if (isExpired) {
        const popupType: 'PAY' | 'LIMIT' | 'UPGRADE' =
          ['Starter', 'Growth', 'ScaleX'].includes(membershipType) ? 'LIMIT' : 'PAY';
        this.planService.expiredPopup('ACTIVE_INTERACTIONS', popupType, null, null, null, null,
          availablePoints, actualPoints, utilizedPoints, promotional);
      } else {
        this.util.stopLoader();
        this.chatuser(seletedItem);
      }
    } catch (error) {
      console.error('Error checking interaction:', error);
    } finally {
      setTimeout(() => this.util.stopLoader(), 1500);
    }
  }

  chatuser(seletedItem: any): void {
    // Owner shortcut — if viewer owns the candidate, skip the connection check.
    if (seletedItem?.createdBy == this.viewerId) {
      this.openMessage(seletedItem);
      return;
    }
    this.checkConnectionStatus(seletedItem).subscribe({
      next: (_response: any) => {
        // Talent Pool candidates ship `isFeatured = null` (not false/undefined),
        // so the original explicit `=== false || === undefined` check missed
        // them and the chat never opened. Treat any falsy value (false, null,
        // undefined) as "not featured" so all non-own non-featured talents
        // open the chat consistently with Job Center / Talent Center.
        if (seletedItem?.isFeatured === true) {
          this.openMessage(seletedItem);
        } else if (!seletedItem?.isFeatured && seletedItem?.createdBy != this.viewerId) {
          this.openMessage(seletedItem);
        }
      },
      error: (_err: any) => {
        this.openMessage(seletedItem);
      }
    });
  }

  openMessage(seletedItem: any): void {
    // Chat is confirmed to open (interaction/plan check passed) — activate the
    // Chat tab now. Deferred to here so an expired-plan popup leaves the user
    // on their current tab instead of an empty skeleton pane.
    this.activeTab = 'chat';
    // Reset first to force the widget to re-create with fresh data.
    this.messagemodelflag = false;

    seletedItem.owner = seletedItem?.createdBy == this.viewerId;
      this.chatLoadingIsOwner = seletedItem.owner;

    // 1s delay mirrors the listing — gives the widget a beat to clean up
    // the previous instance's WebSocket before the new one connects.
    setTimeout(() => {
      seletedItem.userId = seletedItem.createdBy;
      seletedItem.groupType = 'CANDIDATE';
      seletedItem.messageType = 'CANDIDATE';
      seletedItem.id = seletedItem.candidateId;

      this.seletedChatItem = seletedItem;
      this.messageData = seletedItem;
      this.messagemodelflag = true;
    }, 1000);
  }

  closeMessage(_event?: any): void {
    this.messagemodelflag = false;
    // The widget lives inside the Chat tab pane now — closing it would
    // leave an empty pane, so bounce back to the profile tab.
    if (this.activeTab === 'chat') {
      this.activeTab = 'profile';
    }
  }

  onClose(): void { this.closeRequested.emit(); }

  // ── View helpers (Tab 1) ──────────────────────────────────────────────

  get candidateName(): string {
    const c = this.candidate || {};
    const f = (c.firstName || '').trim();
    const l = (c.lastName || '').trim();
    if (f || l) return [f, l].filter(Boolean).join(' ');
    return (c.fullname || c.fullName || c.name || 'Candidate').toString().trim();
  }
  get candidateInitials(): string {
    const c = this.candidate || {};
    const f = (c.firstName || '').trim();
    const l = (c.lastName || '').trim();
    if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
    // Derive from fullname when firstName/lastName missing.
    const full = (c.fullname || c.fullName || c.name || '').toString().trim();
    if (!full) return '?';
    const parts = full.split(/\s+/);
    const a = parts[0]?.[0] ?? '';
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
    return (a + b).toUpperCase() || '?';
  }
  get candidatePhoto(): string | null {
    const photo = this.candidate?.photo;
    if (!photo) return null;
    return photo.startsWith('http') ? photo : (this.url + 'download/' + photo);
  }
  get candidateLocation(): string {
    const c = this.candidate;
    return [c?.city, c?.state, c?.country].filter(Boolean).join(', ');
  }
  get candidateCid(): string {
    return this.candidate?.candidateReferenceId || this.candidate?.candidateId || '';
  }
  get titleBadge(): { label: string; tone: 'success' | 'warn' | 'neutral' | 'danger'; icon?: string; glyph?: string } | null {
    // Mirror the candidate-card list pill (candidates-page.component.html
    // lines 597-624): ACTIVE → Available, INACTIVE → Unavailable,
    // BLOCKED → Blocked, DRAFTED → Draft, AWAITING HOST → Awaiting Post.
    const c = this.candidate || {};
    const status = (c.status || '').toString().toUpperCase();
    const availability = c.availability;
    if (status === 'ACTIVE' && availability !== false) {
      // Mock parity (.cp-hdr-avail, "⚡ Available"): the wireframe uses the
      // raw emoji, not a stroked SVG bolt.
      return { label: 'Available', tone: 'success', glyph: '⚡' };
    }
    if (status === 'INACTIVE' || availability === false) {
      // Red like the V3 rows' INACTIVE pill (shared status palette), not grey.
      return { label: 'Unavailable', tone: 'danger', icon: 'tablerClock' };
    }
    if (status === 'BLOCKED') {
      return { label: 'Blocked', tone: 'danger' };
    }
    if (status === 'DRAFTED') {
      return { label: 'Draft', tone: 'neutral' };
    }
    if (status === 'AWAITING HOST') {
      return { label: 'Awaiting Post', tone: 'neutral' };
    }
    return null;
  }

  /** Remaining upgrade (featured) days for the candidate, or null when not
   *  upgraded / expired. Mirrors candidate-profile-summary.component.ts:1172
   *  so the slider matches the candidate-detail page's gating exactly. */
  private getActiveUrgencyDays(candidate: any): number | null {
    if (!candidate?.isFeatured) return null;
    const now = new Date();
    let urgencyEndDate: Date;
    if (candidate?.featuredConsumptions?.length > 0) {
      const lastConsumption = candidate.featuredConsumptions[candidate.featuredConsumptions.length - 1];
      const fromDate = new Date(lastConsumption.fromDate);
      const dayCount = lastConsumption.dayCount || 1;
      urgencyEndDate = new Date(fromDate.getTime() + (dayCount * 24 * 60 * 60 * 1000));
    } else if (candidate?.featuredUpdatedOn) {
      urgencyEndDate = new Date(new Date(candidate.featuredUpdatedOn).getTime() + (2 * 24 * 60 * 60 * 1000));
    } else {
      return 1;
    }
    const days = Math.ceil((urgencyEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 1;
  }

  /** Upgrade badge for the sticky header — "Hot" with NO countdown, matching
   *  the candidate-detail page (which shows the badge only). Null when not
   *  upgraded. */
  get upgradeBadge(): { label: string; countdown?: string } | null {
    const days = this.getActiveUrgencyDays(this.candidate);
    if (days == null) return null;
    return { label: 'Hot' };
  }
  get headerMeta(): Array<{ icon: string; text: string; href?: string }> {
    const c = this.candidate;
    const items: Array<{ icon: string; text: string; href?: string }> = [];
    const loc = this.candidateLocation;
    if (loc) items.push({ icon: 'tablerMapPin', text: loc });
    if (c?.phone) items.push({ icon: 'tablerPhone', text: c.phone, href: `tel:${c.phone}` });
    // Email — wireframe (`gigsumo_job_center_v2(16).html:1692`) masks
    // the actual address as the literal "[email protected]" placeholder
    // (Cloudflare email-obfuscation pattern). We only render the icon +
    // placeholder when the candidate actually has an email so the row
    // doesn't show "[email protected]" for candidates without one. No
    // `href` is set, so the anchor in cp-header renders as a non-
    // clickable element (parity with the wireframe's masked anchor).
    const emailValue = c?.candidateEmailId || c?.email || c?.user?.username;
    // Wireframe (`gigsumo_job_center_v2(16).html:1692`) masks the email
    // as the literal "[email protected]" placeholder rendered as an
    // `<a>` so it reads as a link. `linkLike: true` triggers the
    // purple/underline link style in cp-header without setting `href`
    // (no actual click target).
    if (emailValue) {
      items.push({ icon: 'tablerMail', text: 'email protected' });
    }
    return items;
  }
  get matchScore(): number | null {
    // `matchingScore` is set on job-applicants payloads; talent-pool /
    // suggested-candidates put the same value on `score` (see
    // candidate-card-table-view.component.html:97-100 and :401-409 for
    // the symmetric fallback the card list already does). Without the
    // `score` fallback the cp-header ring never renders for those
    // surfaces, even when the AI Insights tab returns a real value.
    const raw = this.candidateSummary?.matchingScore
      ?? this.candidate?.matchingScore
      ?? this.candidateSummary?.score
      ?? this.candidate?.score;
    if (raw === undefined || raw === null) return null;
    const n = Number(raw);
    return isNaN(n) ? null : Math.round(n);
  }
  get hasResume(): boolean {
    const list = this.candidate?.resumes;
    return Array.isArray(list) && list.length > 0 && !!list[0]?.fileId;
  }

  get headerActions() {
    // Mock parity (gigsumo_job_center_v2(16).html lines 1701-1705):
    //   - Download Resume  → primary purple (filled)
    //   - Chat             → outline (white bg, gray border)
    //   - Share            → outline
    // Earlier code set every button to `primary`, which dyed the whole
    // action row purple and made the secondary buttons read as part of
    // the same action — the wireframe deliberately separates the
    // primary "Download Resume" CTA from the two outline buttons.
    const actions: Array<{ key: string; label: string; icon: string; tone: 'primary' | 'outline'; disabled?: boolean; spinner?: boolean }> = [];
    if (this.hasResume) {
      // While downloading, label + icon stay untouched — the faded fill
      // and centered spinner overlay (.jc-v3-btn-downloading) carry the
      // in-progress state.
      actions.push({
        key: 'download',
        label: 'Download Resume',
        icon: 'tablerDownload',
        tone: 'primary',
        disabled: this.resumeDownloading,
        spinner: this.resumeDownloading,
      });
    }
    // Chat with the candidate does not require a peer connection — the
    // connected-status gate only applies to the rec-strip's personal chat
    // with the submitter (which routes to the message page). Always offer
    // the header Chat action here — except where chat is hidden outright
    // (Contract Center / Billing Center, see chatHidden).
    if (!this.chatHidden) {
      actions.push({ key: 'chat', label: 'Chat', icon: 'tablerMessage', tone: 'outline', disabled: this.chatDisabled });
    }
    actions.push({ key: 'share', label: 'Share', icon: 'tablerShare', tone: 'outline' });
    return actions;
  }

  /** True when the slider was opened from an org candidate page (Talent
   *  Center or Talent Pool). Drives the chat-after-connection gating on
   *  both the header Chat action and the rec-strip Chat icon. Talent
   *  Center tags 'talent-center', Talent Pool tags 'talent-pool'. */
  get isOrgCandidateView(): boolean {
    return this.pageFrom === 'talent-center' || this.pageFrom === 'talent-pool';
  }

  // ── Rate formatting helpers ──────────────────────────────────────────
  // Mirrors `FormValidation.getCurrency` / `getPayType` + the listing's
  // `candidates-page.component.html:659` markup so the slider's rate
  // strings format identically to the candidate cards ("$ 55,000 USD /
  // Month"), with `| number` style comma separators added.

  private getCurrencyForCountry(country: string): string {
    switch (country) {
      case 'US': return 'USD';
      case 'IN': return 'INR';
      case 'CA': return 'CAD';
      case 'AU': return 'AUD';
      default:   return 'CUR';
    }
  }

  private getPayPeriod(payType: string): string {
    switch (payType) {
      case 'Hourly':  return 'Hr';
      case 'Daily':   return 'Day';
      case 'Weekly':  return 'Week';
      case 'Monthly': return 'Month';
      case 'Yearly':  return 'Year';
      default:        return '';
    }
  }

  private formatRateAmount(amount: any): string {
    if (amount == null || amount === '') return '';
    const n = Number(amount);
    if (!isFinite(n)) return amount.toString();
    return n.toLocaleString('en-US');
  }

  /** "55,000 USD / Month" — amount + currency + period. The "$" is not
   *  prefixed here because the surrounding card already renders a `$`
   *  glyph before the "Submitted for: / Pay Rate:" label, and the
   *  "Pay Rate:" aux on the right is plain text. Repeating the symbol
   *  reads as a duplication. The currency suffix (USD/INR/etc) keeps
   *  the unit unambiguous. */
  private buildRateString(amount: any, payType: string, country: string): string {
    const formatted = this.formatRateAmount(amount);
    if (!formatted) return '';
    const currency = this.getCurrencyForCountry(country);
    const period = this.getPayPeriod(payType);
    return `${formatted} ${currency}${period ? ' / ' + period : ''}`.trim();
  }

  /** "X,XXX – Y,YYY USD / Month" — range variant of `buildRateString`. */
  private buildRateRangeString(lo: any, hi: any, payType: string, country: string): string {
    const loFmt = this.formatRateAmount(lo);
    const hiFmt = this.formatRateAmount(hi);
    if (!loFmt || !hiFmt) return '';
    const currency = this.getCurrencyForCountry(country);
    const period = this.getPayPeriod(payType);
    return `${loFmt} – ${hiFmt} ${currency}${period ? ' / ' + period : ''}`.trim();
  }

  /** "Submitted for: $ X,XXX USD / Month" — the rate at which the
   *  candidate was submitted to the *currently-open job*. The backend
   *  ships the per-job submission record under `candidate.jobsApplied[]`,
   *  where each entry's `userId` holds the *jobId* (odd naming, but
   *  matches the canonical lookup at
   *  `candidate-card.component.ts:1751-1752`). We match on the slider's
   *  `jobId` input and pull `targetRate` + `payType` off the matched
   *  entry — that's the submission rate for THIS job, not the
   *  candidate's own target range (which `payRateRange` covers).
   *
   *  Returns empty when the slider is opened without a `jobId` context
   *  (e.g. from the candidate listing), so the template falls back to
   *  showing only the candidate's pay-rate range. */
  get submittedForDisplay(): string {
    const c = this.candidate;
    if (!c || !this.jobId) return '';
    const jobsApplied: any[] = Array.isArray(c.jobsApplied) ? c.jobsApplied : [];
    const match = jobsApplied.find((j: any) => j?.userId === this.jobId);
    if (!match) return '';
    return this.buildRateString(match.targetRate, match.payType, c.country);
  }

  /** "$ lo – hi USD / Month" — the JOB's posted rate range when the slider is
   *  opened from a job context (`jobData`), so "Pay Rate" reflects the job,
   *  not the candidate. Falls back to the candidate's own target range when
   *  there's no job context (e.g. opened from the candidate listing). */
  get payRateRange(): string {
    const j = this.jobData;
    if (j) {
      const country = j.country ?? this.candidate?.country;
      if (j.targetRateFrom != null && j.targetRateTo != null) {
        return this.buildRateRangeString(j.targetRateFrom, j.targetRateTo, j.payType, country);
      }
      if (j.targetRate != null) {
        return this.buildRateString(j.targetRate, j.payType, country);
      }
    }
    const c = this.candidate;
    if (!c) return '';
    const lo = c.targetRateFrom;
    const hi = c.targetRateTo;
    if (lo != null && hi != null) {
      return this.buildRateRangeString(lo, hi, c.payType, c.country);
    }
    if (c.targetRate != null) {
      return this.buildRateString(c.targetRate, c.payType, c.country);
    }
    return '';
  }

  /** Back-compat alias kept for any external template still binding `payRateDisplay`. */
  get payRateDisplay(): string { return this.submittedForDisplay; }

  /** Current employer name for the Quick Facts grid. Per the approved
   *  wireframe this is the candidate's Work Experience entry shown as
   *  "Present" — i.e. `currentOrganization === true`, the same flag the
   *  Work Experience card below renders (and the V2 profile page's
   *  "Present" pill at candidates-profile-details.component.html:111).
   *  Legacy top-level keys remain as fallback for payloads that don't
   *  ship `workExperiences[]`. */
  get currentEmployer(): string {
    const c: any = this.candidate || {};
    const wxp: any[] = Array.isArray(c.workExperiences) ? c.workExperiences : [];
    const present = wxp.find((w: any) => w?.currentOrganization === true && w?.organisationName);
    if (present) {
      return present.organisationName.toString().trim();
    }
    return (c.currentEmployer || c.currentOrganisation || c.currentOrganization || c.employer || '').toString().trim();
  }

  // ── "Submitted by" strip — submitter (recruiter / bench-sales user) ──
  //
  // Field shape mirrors the V3 candidate detail page
  // (`new-candidate.component.html` line 277-280): the API ships a
  // `user` sub-object on the candidate with `fullname` + `userType`,
  // plus a top-level `postedBehalfOf` for the organisation the
  // candidate was submitted on behalf of. We fall back to the flat
  // `createdByUserName` / `createdByUserType` scalars when the
  // sub-object isn't yet hydrated (initial summary paint).

  private readonly submitterUserTypeMap: { [key: string]: string } = {
    'FREELANCE_RECRUITER': 'Freelance Recruiter',
    'RECRUITER': 'Recruiter',
    'JOB_SEEKER': 'Job Seeker',
    'MANAGEMENT_TALENT_ACQUISITION': 'Manager',
    'MANAGEMENT TALENT ACQUISITION': 'Manager',
    'BENCH_RECRUITER': 'Bench Recruiter',
  };

  get submitterName(): string {
    const c: any = this.candidate || {};
    return (c.user?.fullname || c.createdByUserName || '').toString().trim();
  }

  /** Submitter's (candidate owner's) profile photo for the "Submitted by"
   *  strip. Same source the V3 applicant card uses (`candidate.user.photo`)
   *  and the same URL normalization as `candidatePhoto` above, so the strip
   *  matches the card instead of always falling back to initials. */
  get submitterPhoto(): string {
    const photo = this.candidate?.user?.photo;
    if (!photo) return '';
    return photo.startsWith('http') ? photo : (this.url + 'download/' + photo);
  }

  /** True when the logged-in user is the candidate's owner/submitter
   *  (createdBy === viewerId), regardless of which page opened the slider.
   *  You can't Connect to or chat with your own candidate, so this hides the
   *  rec-strip Connect / Chat actions and shows "You" instead of your own name
   *  on the "Submitted by" strip. Previously this was gated to
   *  pageFrom==='talent-center' only, so on Job → Applied (and elsewhere) your
   *  own candidate still showed Connect/Chat and your name — broadened to pure
   *  ownership. */
  get isOwnCandidate(): boolean {
    const createdBy = this.candidate?.createdBy;
    return !!createdBy && createdBy === this.viewerId;
  }

  get submitterRole(): string {
    const c: any = this.candidate || {};
    const raw = (c.user?.userType || c.createdByUserType || '').toString().trim();
    if (!raw) return '';
    return this.submitterUserTypeMap[raw]
      || raw.replace(/_/g, ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase());
  }

  /** Organisation name shown beside the submitter's role on the
   *  "Submitted by" strip. The canonical source is the submitter's
   *  current employer in their own work-experience list — i.e. the
   *  entry where `currentOrganization === true` — matching the V2
   *  details page's `Ricoh Consulting, Inc` rendering and the listing
   *  card's pattern. Falls back to `postedBehalfOf` while the `user`
   *  sub-object hasn't been hydrated yet. */
  get submitterOrganisation(): string {
    const c: any = this.candidate || {};
    const work = c.user?.workExperience;
    if (Array.isArray(work)) {
      const current = work.find((w: any) => w?.currentOrganization === true);
      if (current?.organisationName) {
        return current.organisationName.toString().trim();
      }
    }
    // Fallback while the user sub-object isn't loaded yet, or when no
    // entry is flagged as currentOrganization.
    const org = c.postedBehalfOf || c.createdByOrganisation || '';
    return org && org !== 'null' ? org.toString().trim() : '';
  }

  /** Candidate Summary rich-HTML for the profile tab. Canonical field is
   *  `resumeSummary` (same as the V2 candidate-profile-summary). Falls
   *  back to `aboutCandidate` if a caller still ships that. Returns '' for
   *  empty summernote placeholders so the *ngIf hides the card. */
  get candidateSummaryHtml(): string {
    const c: any = this.candidate || {};
    const html = (c.resumeSummary || c.aboutCandidate || '').toString().trim();
    if (!html || html === '<p><br></p>' || html === '<br>') return '';
    return html;
  }

  get primarySkills(): string[] {
    const v = this.candidate?.primarySkills;
    return Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
  }
  get secondarySkills(): string[] {
    const v = this.candidate?.secondarySkills;
    return Array.isArray(v) ? v : (typeof v === 'string' ? v.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
  }

  // ── Shortcuts ─────────────────────────────────────────────────────────

  private triggerResumeDownload(): void {
    // Header shortcut still lands the user on the Resume tab (so the
    // in-progress state is visible there too), but now also starts the
    // download itself instead of waiting for a second click on the card.
    this.selectTab('resume');
    this.downloadPrimaryResume();
  }

  /** Saved-as filename for the primary resume — strips the backend's
   *  `<uuid>_` disk prefix (same rule as cp-resume-card's `filename`). */
  private get primaryResumeFileName(): string {
    const resume = Array.isArray(this.candidate?.resumes) ? this.candidate.resumes[0] : null;
    const raw = (resume?.fileName || resume?.name || '').toString().trim();
    if (!raw) return 'resume';
    const uuidPrefix = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;
    return raw.replace(uuidPrefix, '').trim() || 'resume';
  }

  /** Single download flow shared by the header action and the Resume tab
   *  card. `resumeDownloading` gates re-entry so a second click anywhere
   *  is a no-op until the blob resolves.
   *
   *  The ACTIVE_INTERACTIONS gate was MISSING here: the slider called
   *  queryBlob straight away, so a viewer with no interactions left still
   *  downloaded the resume - and the Lambda then charged them for it. The
   *  candidate card has always gated (candidate-card.component.ts
   *  downloadResume), which is why the same button behaves correctly there.
   *  This mirrors the card's rules using the gate this component already
   *  applies to chat in openMessagecheckInteraction. */
  async downloadPrimaryResume(): Promise<void> {
    if (this.resumeDownloading) return;
    const resume = Array.isArray(this.candidate?.resumes) ? this.candidate.resumes[0] : null;
    const fileId = resume?.fileId;
    const candidateId = this.candidate?.candidateId;
    const createdBy = this.candidate?.createdBy;
    if (!candidateId || !this.viewerId || !createdBy || !fileId) return;

    // APPLICANTS MENU ONLY - and this runs FIRST, before the resumeDownloaded check.
    // The job owner invited this candidate for this job, which already consumed one
    // ACTIVE_INTERACTION; the candidate then applied and appears under Applicants, so the
    // resume is already paid for and the plan check must be skipped entirely.
    //
    // It has to come before the free-path check below, not after: `resumeDownloaded` only
    // records DOWNLOADS, so an invited candidate whose resume was downloaded once returned
    // there and this check never ran at all.
    // Scoped by pageFrom + jobId, so the Talent Pool slider (opened with no job) and every
    // other surface are untouched. isInterestShownForJob swallows its own errors, so a
    // failed lookup simply falls through to the normal gate.
    const isApplicantsMenu = this.pageFrom === 'job-applicants' && !!this.jobId;

    if (isApplicantsMenu
        && await this.planService.isInterestShownForJob(candidateId, this.jobId)) {
      this.performResumeDownload(candidateId, createdBy, fileId);
      return;
    }

    // Free paths. The owner never pays for their own candidate, anywhere.
    //
    // `resumeDownloaded` is deliberately NOT a free pass in the APPLICANTS menu: there an
    // INTEREST_NOT_FOUND answer means the slot check always runs, because the interest lookup
    // above - not this flag - decides whether the pair was already paid for. Every other
    // surface keeps the original short-circuit.
    if (createdBy === this.viewerId
        || (!isApplicantsMenu && this.candidate?.resumeDownloaded)) {
      this.performResumeDownload(candidateId, createdBy, fileId);
      return;
    }

    this.resumeDownloading = true;
    try {
      // No message/contact/exist call here, deliberately. The card's downloadResume
      // does not make one either: for a DOWNLOAD the `resumeDownloaded` flag checked
      // above already says whether this pair was charged, so the extra round-trip
      // would tell us nothing new. isInteractionAlreadyConsumed stays on the chat
      // path (openMessagecheckInteraction), where there is no such flag.
      this.util.startLoader();
      const userData: any = await this.planService.UPDATE_USERDETAILS('ACTIVE_INTERACTIONS');
      const { userDetail, isExpired, membershipType } = userData || {};
      let availablePoints = null;
      let actualPoints = null;
      let utilizedPoints = null;
      let promotional = null;

      if (userDetail?.userPlanAndBenefits?.benefitsUsages) {
        userDetail.userPlanAndBenefits.benefitsUsages.forEach((ele: any) => {
          if (ele.benefitKey === 'ACTIVE_INTERACTIONS') {
            availablePoints = ele.available;
            actualPoints = ele.actual;
            utilizedPoints = ele.utilized;
            promotional = ele.promotional;
          }
        });
      } else {
        // No usage block to read - fail open rather than block the download,
        // which is what openMessagecheckInteraction does for chat.
        this.util.stopLoader();
        this.performResumeDownload(candidateId, createdBy, fileId);
        return;
      }

      if (isExpired) {
        const popupType: 'PAY' | 'LIMIT' | 'UPGRADE' =
          ['Starter', 'Growth', 'ScaleX'].includes(membershipType) ? 'LIMIT' : 'PAY';
        this.planService.expiredPopup('ACTIVE_INTERACTIONS', popupType, null, null, null, null,
          availablePoints, actualPoints, utilizedPoints, promotional);
        // The lock MUST be released before leaving. Returning with it still set
        // left the button permanently disabled once the plan ran out - the bug
        // called out in candidate-card's downloadResume.
        this.resumeDownloading = false;
        return;
      }

      this.util.stopLoader();
      this.performResumeDownload(candidateId, createdBy, fileId);
      // Remember it locally so a second click in this same slider session takes
      // the free path above instead of spending another interaction.
      if (this.candidate) {
        this.candidate.resumeDownloaded = true;
      }
    } catch (error) {
      console.error('Error checking interaction before resume download:', error);
      this.resumeDownloading = false;
    } finally {
      setTimeout(() => this.util.stopLoader(), 1500);
    }
  }

  /** The actual blob fetch + save. Only reached once the plan gate has passed. */
  private performResumeDownload(candidateId: any, createdBy: any, fileId: any): void {
    this.resumeDownloading = true;
    const endpoint = `candidates/resumeDownload/${candidateId}/${this.viewerId}/${createdBy}/${fileId}`;
    this.apiService.queryBlob(endpoint).subscribe({
      next: (res: any) => {
        const blob = new Blob([res]);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = this.primaryResumeFileName;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 0);
        this.resumeDownloading = false;
      },
      error: () => { this.resumeDownloading = false; }
    });
  }

  trackByIndex(i: number): number { return i; }
  trackByKey(i: number, o: any): any { return o?.id ?? o?.key ?? i; }
}
